import React, { useMemo, useRef } from 'react';

import { type Table } from '@tanstack/react-table';

import { Box, type BoxProps } from '@atlaskit/primitives/compiled';

import { useAutoScrollForReordering } from '../hooks/reordering-monitors/use-autoscroll-for-reordering';
import { useDragDropMonitor } from '../hooks/reordering-monitors/use-drag-drop-monitor';
import { useTableHeightEffect } from '../hooks/table-effects/use-table-height-effect';
import { useTablePinningEffects } from '../hooks/table-effects/use-table-pinning-effects';
import { useMergeRefs } from '../hooks/use-merge-refs';

interface CharlieTableDndWrapperProps<T extends object> extends BoxProps<'div'> {
	charlieTableState: Table<T>;
	children: React.ReactNode;
	tableRef: React.RefObject<HTMLTableElement>;
	tbodyRef: React.RefObject<HTMLTableSectionElement>;
	enableRowReorder?: boolean;
	enableColumnReorder?: boolean;
	onDraggingRowEnd?: (args: { table: Table<T>; draggingRow: any; destinationRow: any }) => void;
}

export const cssVarTableHeight = '--table-height';
export const cssPinningBackgroundLeft = '--pinning-background-left';
export const cssPinningBackgroundRight = '--pinning-background-right';

const getLeftPinnedColumnsWidth = <T extends object>(charlieTableState: Table<T>): number => {
	const leftPinnedColumns = charlieTableState.getLeftVisibleLeafColumns();
	let totalWidth = 0;
	for (const column of leftPinnedColumns) {
		totalWidth += column.getSize();
	}
	return totalWidth;
};

const getRightPinnedColumnsWidth = <T extends object>(charlieTableState: Table<T>): number => {
	const rightPinnedColumns = charlieTableState.getRightVisibleLeafColumns();
	let totalWidth = 0;
	for (const column of rightPinnedColumns) {
		totalWidth += column.getSize();
	}
	return totalWidth;
};

export const CharlieTableDndWrapper: React.ForwardRefExoticComponent<
	Omit<CharlieTableDndWrapperProps<object>, 'ref'> & React.RefAttributes<HTMLDivElement>
> = React.forwardRef(
	<T extends object>(
		{
			charlieTableState,
			children,
			tableRef,
			tbodyRef,
			enableRowReorder = false,
			enableColumnReorder = false,
			onDraggingRowEnd,
			...props
		}: CharlieTableDndWrapperProps<T>,
		ref: CharlieTableDndWrapperProps<T>['ref'],
	) => {
		const internalRef = useRef<HTMLDivElement | null>(null);
		const mergedRef = useMergeRefs(internalRef, ref);

		// calculate pinned column widths for autoscroll positioning
		const columnPinning = charlieTableState.getState().columnPinning;
		const columnSizing = charlieTableState.getState().columnSizing;
		const leftPinnedColumnsWidth = useMemo(
			() => getLeftPinnedColumnsWidth(charlieTableState),
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[columnPinning?.left, columnSizing],
		);
		const rightPinnedColumnsWidth = useMemo(
			() => getRightPinnedColumnsWidth(charlieTableState),
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[columnPinning?.right, columnSizing],
		);

		useTableHeightEffect({ tableRef });
		useTablePinningEffects({
			tableRef,
			wrapperRef: internalRef,
			tbodyRef,
			charlieTableState,
		});
		useAutoScrollForReordering({
			wrapperRef: internalRef,
			enableColumnReorder,
			enableRowReorder,
			leftPinnedColumnsWidth,
			rightPinnedColumnsWidth,
		});
		useDragDropMonitor({
			tableRef,
			charlieTableState,
			enableColumnReorder,
			enableRowReorder,
			onDraggingRowEnd,
		});

		return (
			<Box ref={mergedRef} {...props}>
				{children}
			</Box>
		);
	},
);
